/**
 * Team Column
 *
 * Vertical roster of players for a single team.
 * Used in the 2-team flanking layout.
 */

import type { QueueRankConfig } from "@rivaled/shared";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils";
import { PlayerCard } from "../components/PlayerCard";
import { TeamHeader } from "../components/TeamHeader";
import { useCurrentPlayerId } from "../hooks/useCurrentPlayerId";
import { getPartyColor } from "../lib/party-colors";
import type { PlayerStatusFn } from "./getNodePlayerStatus";
import type { TeamData } from "./types";

type TeamColumnProps = {
  team: TeamData;
  myPartyId: string | null;
  rankConfig: QueueRankConfig | null;
  side: "left" | "right";
  getPlayerStatus?: PlayerStatusFn;
};

export function TeamColumn({
  team,
  myPartyId,
  rankConfig,
  side,
  getPlayerStatus,
}: TeamColumnProps) {
  const currentPlayerId = useCurrentPlayerId();

  return (
    <motion.div
      initial={{ opacity: 0, x: side === "left" ? -16 : 16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className={cn(
        "flex flex-col rounded-lg border overflow-hidden min-w-0",
        team.isMyTeam
          ? "bg-surface-800/60 border-brand-500/25 shadow-[0_0_12px_rgba(38,217,181,0.06)]"
          : "bg-surface-800/60 border-surface-700/30",
      )}
    >
      <TeamHeader teamName={team.teamName} isMyTeam={team.isMyTeam} />
      <div className="flex flex-col gap-1 p-2">
        {team.players.map((player, i) => {
          const status = getPlayerStatus?.(player.userId);
          return (
            <PlayerCard
              key={player.userId}
              player={player}
              variant="compact"
              isMe={player.userId === currentPlayerId}
              partyColor={getPartyColor(player.partyId, myPartyId)}
              rankConfig={rankConfig}
              animationDelay={i * 0.05}
              animateFrom={side === "left" ? "left" : "right"}
              rightSlot={status?.rightSlot}
              className={status?.className}
            />
          );
        })}
      </div>
    </motion.div>
  );
}
